import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { SessionMembership } from '@orienta/shared';
import { useSession } from '@/features/auth/session';

const KEY = 'orienta-inst';

interface InstitutionContextState {
  /** Instituição selecionada no seletor de contexto (null = todas). */
  institutionId: string | null;
  membership: SessionMembership | null;
  memberships: SessionMembership[];
  setInstitutionId: (id: string | null) => void;
}

const Ctx = createContext<InstitutionContextState | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function InstitutionProvider({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  const [params, setParams] = useSearchParams();
  const memberships = useMemo(() => session?.memberships ?? [], [session]);
  const [stored, setStored] = useState<string | null>(() => params.get('inst') ?? readStored());

  const valid = (id: string | null) => (id && memberships.some((m) => m.institutionId === id) ? id : null);
  const institutionId = valid(params.get('inst')) ?? valid(stored) ?? (memberships.length === 1 ? memberships[0]!.institutionId : null);

  const setInstitutionId = useCallback(
    (id: string | null) => {
      setStored(id);
      try {
        if (id) localStorage.setItem(KEY, id);
        else localStorage.removeItem(KEY);
      } catch {
        /* armazenamento indisponível */
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('inst', id);
          else next.delete('inst');
          // filtros dependentes da instituição deixam de valer
          next.delete('programId');
          next.delete('page');
          if (next.has('institutionId')) {
            if (id) next.set('institutionId', id);
            else next.delete('institutionId');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const value = useMemo(
    () => ({
      institutionId,
      membership: memberships.find((m) => m.institutionId === institutionId) ?? null,
      memberships,
      setInstitutionId,
    }),
    [institutionId, memberships, setInstitutionId],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook do provider definido neste módulo
export function useInstitutionContext() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useInstitutionContext fora do provider');
  return c;
}
